import React, { useEffect, useState } from "react";
import Select from "react-select";
import "../styles/AddItemForm.css";
import "../styles/GRNFormRadio.css";
import "../styles/grnformvalidation.css";
import { getData } from "../api/ApiServices";
import { ApiResponse } from "../models/ItemModelTypes";
import { RefreshCw } from "lucide-react";
import { useSelector } from "react-redux";
import { RootState } from "../redux/slices/store";
import { GrnUtils } from "../utils/grn/grnUtils";
import { PurchaseOrderUtils } from "../utils/purchaseOrder/purchaseOrderutils";

interface PurchaseOrderFormProps {
    onSave: (poData: any) => Promise<void>;
    onCancel: () => void;
    isProcessing?: boolean;
    isEditing?: boolean;
    editingPO?: any | null;
    poNumber: string;
    prefilledData: { supplierId: string; poNumber: string; readOnly: boolean; paymentTerms: string; dueDate: string };
    isReadOnly: boolean;
}

const PurchaseOrderForm: React.FC<PurchaseOrderFormProps> = ({
    onSave,
    onCancel,
    isProcessing,
    isEditing,
    editingPO,
    poNumber,
    prefilledData,
    isReadOnly
}) => {
    const getTomorrowDate = () => {
        const tomorrow = new Date();
        tomorrow.setDate(tomorrow.getDate() + 1);
        return tomorrow.toISOString().split("T")[0];
    };

    const initialState = {
        poNumber: "",
        poDate: new Date().toISOString().split("T")[0],
        supplierId: "",
        partId: "",
        qty: 0,
        unitPrice: 0,
        total: 0,
        gst: 0,
        sgst: 0,
        cgst: 0,
        igst: 0,
        gstTotal: 0,
        netTotal: 0,
        dueDate: getTomorrowDate(),
        paymentTerms: "",
    };

    const initialPartState = {
        partId: "",
        partName: "",
        model: "",
        category: "",
        uom: "",
        unitPrice: 0,
        packingQty: 0,
        gstPercentage: 0,
        sgstPercentage: 0,
        cgstPercentage: 0,
        igstPercentage: 0,
    };

    const { supplierOptions, fetchSupplierOptions, supplierParts, fetchSupplierParts, fetchOnSearchPartDetails, partOptions } = PurchaseOrderUtils();


    useEffect(() => {
        fetchSupplierOptions("");
    }, []);

    const { readOnly } = prefilledData;
    const [searchTerm, setSearchTerm] = useState<string>("");
    const [poData, setPoData] = useState(initialState);

    const permission = useSelector((state: RootState) => state.permissions);

    const [selectedPart, setSelectedPart] = useState(initialPartState);
    const [errors, setErrors] = useState({
        supplierId: "",
        partId: "",
        qty: "",
        dueDate: "",
        paymentTerms: ""
    });

    const [formSubmitted, setFormSubmitted] = useState(false);
    const [orderItems, setOrderItems] = useState<any[]>([]);

    const handleRefresh = () => {
        window.location.reload();
    };


    useEffect(() => {
        if (prefilledData) {
            setPoData(prev => ({
                ...prev,
                poNumber,
                supplierId: prefilledData.supplierId || "",
                paymentTerms: prefilledData.paymentTerms,
                dueDate: prefilledData.dueDate ? formatDateForInput(prefilledData.dueDate) : getTomorrowDate()
            }));
        }
    }, [poNumber, prefilledData]);

    const formatDateForInput = (dateString: string) => {
        if (!dateString) return "";
        const date = new Date(dateString);
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, "0");
        const day = String(date.getDate()).padStart(2, "0");
        return `${year}-${month}-${day}`;
    };

    const formatToYearMonDay = (dateStr: string): string => {
        const [day, month, year] = dateStr.split("-");
        const dateObj = new Date(`${year}-${month}-${day}`);
        const options: Intl.DateTimeFormatOptions = {
            year: 'numeric',
            month: 'short',
            day: '2-digit',
        };
        return dateObj.toLocaleDateString('en-GB', options).replace(',', '');
    };

    useEffect(() => {
        console.log(editingPO);

        if (isEditing && editingPO) {
            const quantity = Number(editingPO.qty);
            const total = quantity * editingPO.unitPrice;
            const sgstAmount = (total * editingPO.sgst) / 100;
            const cgstAmount = (total * editingPO.cgst) / 100;
            const igstAmount = (total * editingPO.igst) / 100;
            const gstTotal = sgstAmount + cgstAmount + igstAmount;

            setPoData({
                poNumber: editingPO.poNumber || poNumber,
                poDate: formatToYearMonDay(editingPO.poDate),
                supplierId: editingPO.supplierId || "",
                partId: editingPO.partId || "",
                qty: editingPO.qty || 0,
                unitPrice: editingPO.unitPrice || 0,
                gst: editingPO.gst,
                sgst: editingPO.sgst || 0,
                cgst: editingPO.cgst || 0,
                igst: editingPO.igst || 0,
                total: total || 0,
                gstTotal: gstTotal || 0,
                netTotal: total + gstTotal || 0,
                dueDate: formatDateForInput(editingPO.dueDate),
                paymentTerms: editingPO.paymentTerms || "",
            });

            const selectedPartOption = partOptions.find(part => part.value === editingPO.partId);

            setSelectedPart(prev => ({
                ...prev,
                partName: selectedPartOption.partName
            }))
        }
    }, [isEditing, editingPO]);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;

        if (name === "qty") {
            if (value === "") {
                setPoData((prev) => ({
                    ...prev,
                    [name]: name === "qty" || name === "unitPrice" ? 0 : 0,
                    total: 0,
                    gstTotal: 0,
                    netTotal: 0
                }));
            }
            else if (/^\d+$/.test(value) && value.length <= 5) {
                const quantity = Number(value);
                const total = quantity * poData.unitPrice;
                const sgstAmount = (total * selectedPart.sgstPercentage) / 100;
                const cgstAmount = (total * selectedPart.cgstPercentage) / 100;
                const igstAmount = (total * selectedPart.igstPercentage) / 100;
                const gstTotal = sgstAmount + cgstAmount + igstAmount;

                setPoData((prev) => ({
                    ...prev,
                    qty: quantity,
                    total: total,
                    gstTotal: gstTotal,
                    netTotal: total + gstTotal
                }));
            }
        } else {
            setPoData((prev) => ({
                ...prev,
                [name]: value,
            }));
        }

        if (name in errors) {
            setErrors(prev => ({
                ...prev,
                [name]: ""
            }));
        }
    };

    const handleSelectChange = async (selectedOption: any, { name }: any) => {
        const selectedValue = selectedOption?.value || "";

        setPoData((prev) => ({
            ...prev,
            [name]: selectedOption?.value || "",
        }));

        if (name in errors) {
            setErrors(prev => ({
                ...prev,
                [name]: ""
            }));
        }

        if (name === "supplierId" && selectedValue) {
            try {

                const response = await fetchSupplierParts(selectedValue)
            } catch (error) {
                console.error("Error fetching supplier parts:", error);
            }
        }
    };

    const handlePartChange = (selectedOption: any) => {

        setPoData((prev) => ({
            ...prev,
            partId: selectedOption.value,
            uom: selectedOption.uom,
            unitPrice: selectedOption.unitPrice || 0,
            total: 0,
            gst: selectedOption.gst,
            sgst: selectedOption.sgst,
            cgst: selectedOption.cgst,
            igst: selectedOption.igst,
            gstTotal: 0,
            netTotal: 0,
            qty: 0
        }));

        console.log("partoptions:", partOptions);

        const selectedPartData = partOptions.find(p => p.value === selectedOption.value);

        if (selectedPartData) {
            setSelectedPart({
                partId: selectedPartData.value,
                partName: selectedPartData.partName,
                model: selectedPartData.model,
                category: selectedPartData.category,
                uom: selectedPartData.uom,
                unitPrice: selectedPartData.unitPrice,
                packingQty: selectedPartData.packingQty || 0,
                gstPercentage: selectedPartData.gst,
                sgstPercentage: selectedPartData.sgst || 0,
                cgstPercentage: selectedPartData.cgst || 0,
                igstPercentage: selectedPartData.igst || 0,
            });
        }

        setErrors(prev => ({
            ...prev,
            partId: ""
        }));
    };

    const validateForm = () => {
        const newErrors = {
            supplierId: !poData.supplierId ? "Supplier is required" : "",
            partId: !poData.partId ? "Part number is required" : "",
            qty: poData.qty === 0 ? "Quantity is required" :
                Number(poData.qty) <= 0 ? "Quantity must be greater than 0" :
                    String(poData.qty).length > 5 ? "Quantity must not exceed 5 digits" : "",
            dueDate: !poData.dueDate ? "Due date is required" :
                new Date(poData.dueDate) <= new Date() ? "Due date must be in the future" : "",
            paymentTerms: !poData.paymentTerms ? "Payment terms are required" : ""
        };

        setErrors(newErrors);
        return Object.values(newErrors).every(error => error === "");
    };

    const getTotalAmount = () => {
        return orderItems.reduce((sum, item) => sum + (item.netTotal || 0), 0);
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setFormSubmitted(true);

        if (!validateForm()) {
            return;
        }

        if (editingPO) {
            setPoData((prev) => ({
                ...prev,
                poDate: formatDate(poData.poDate)
            }))
        }

        const formatDate = (date: string) => {
            return date && !isNaN(new Date(date).getTime())
                ? new Date(date).toISOString().split("Z")[0]
                : new Date().toISOString().split("Z")[0];
        };

        try {
            const { total, netTotal, gstTotal, ...poDataToSave } = poData;

            // console.log(poDataToSave);
            await onSave(poDataToSave);

            setPoData({
                ...initialState,
                partId: "",
            });

            setSelectedPart((prev) => ({
                ...prev,
                partName: ""
            }))
        } catch (error) {
            console.error("Error submitting PO form:", error);
        }
    };

    const handleCancel = () => {
        setPoData({
            ...initialState,
            poNumber: poNumber || "",
            poDate: new Date().toISOString().split("T")[0],
            supplierId: prefilledData.supplierId || ""
        });

        setSelectedPart(initialPartState);
        setOrderItems([]);

        setErrors({
            supplierId: "",
            partId: "",
            qty: "",
            dueDate: "",
            paymentTerms: ""
        });

        setFormSubmitted(false);
        onCancel();
    };

    const today = new Date().toISOString().split("T")[0];

    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    const minDate = tomorrow.toISOString().split("T")[0];

    return (
        <>
            <div className="item-models-header">
                <div className="border grn-header">
                    <div className="start-end-layout">
                        <div className="breadcrumb">
                            <span className="breadcrumb-current" onClick={onCancel}>
                                Purchase Orders
                            </span>
                            <span className="breadcrumb-separator"> &gt; </span>
                            <span className="breadcrumb-item">{isEditing ? "Edit Purchase Order" : "New Purchase Order"}</span>
                        </div>
                    </div>
                </div>
            </div>

            <div className="form-container grn-form">
                <form onSubmit={handleSubmit} className="add-item-form">
                    <div className="radio-row-container" style={{ justifyContent: 'end' }}>
                        <div className="form-group2 refresh-container">
                            <button
                                type="button"
                                className="refresh-button"
                                onClick={handleRefresh}
                                title="Refresh"
                            >
                                <RefreshCw size={18} />
                            </button>
                        </div>
                    </div>

                    <div className="form-grid">
                        <div className="form-group">
                            <label>PO No:</label>
                            <div className="input-error-container">
                                <input type="text" name="poNumber" value={poNumber || ""} readOnly className="read-only-input" />
                            </div>
                        </div>

                        <div className="form-group">
                            <label>PO Date:</label>
                            <div className="input-error-container">
                                <input
                                    type="date"
                                    name="poDate"
                                    value={today}
                                    onChange={handleChange}
                                    className={isEditing || isReadOnly ? "read-only-input" : ""}
                                    disabled
                                    max={new Date().toISOString().split("T")[0]}
                                />
                            </div>
                        </div>

                        <div className="form-group">
                            <label>SGST%:</label>
                            <div className="input-error-container">
                                <input
                                    type="text"
                                    value={poData.sgst.toFixed(2) || "0.00"}
                                    readOnly
                                    placeholder="0.00"
                                    className="read-only-input"
                                />
                            </div>
                        </div>

                        <div className="form-group">
                            <label>Supplier Code: <span className="required">*</span></label>
                            <div className="input-error-container">
                                <Select
                                    name="supplierId"
                                    options={supplierOptions}
                                    value={supplierOptions.find((o) => o.value === poData.supplierId) || null}
                                    onChange={(selectedOption) => handleSelectChange(selectedOption, { name: "supplierId" })}
                                    onInputChange={(inputValue) => {
                                        setSearchTerm(inputValue);
                                        fetchSupplierOptions(inputValue);
                                    }}
                                    placeholder="Select Supplier"
                                    className={`react-select-container ${formSubmitted && errors.supplierId ? "error-select" : ""}`}
                                    classNamePrefix="react-select"
                                    isDisabled={isEditing || isReadOnly}
                                />
                                {formSubmitted && errors.supplierId && (
                                    <div className="error-messagegrn">{errors.supplierId}</div>
                                )}
                            </div>
                        </div>

                        <div className="form-group">
                            <label>Unit Price:</label>
                            <div className="input-error-container">
                                <input
                                    type="text"
                                    name="unitPrice"
                                    value={poData.unitPrice || 0}
                                    readOnly
                                    placeholder="Unit Price"
                                    className="read-only-input"
                                />
                            </div>
                        </div>



                        <div className="form-group">
                            <label>CGST%:</label>
                            <div className="input-error-container">
                                <input
                                    type="text"
                                    value={poData.cgst.toFixed(2) || "0.00"}
                                    readOnly
                                    placeholder="0.00"
                                    className="read-only-input"
                                />
                            </div>
                        </div>

                        <div className="form-group">
                            <label>Part No: <span className="required">*</span></label>
                            <div className="input-error-container">
                                <Select
                                    name="partId"
                                    options={partOptions}
                                    value={partOptions.find((o) => o.value === poData.partId) || null}
                                    onChange={handlePartChange}
                                    onInputChange={(inputValue) => {
                                        setSearchTerm(inputValue);
                                        fetchOnSearchPartDetails(inputValue);
                                    }}
                                    placeholder="Select Part No"
                                    className={`react-select-container ${formSubmitted && errors.partId ? "error-select" : ""}`}
                                    classNamePrefix="react-select"
                                    isDisabled={!poData.supplierId || isEditing}
                                />
                                {formSubmitted && errors.partId && (
                                    <div className="error-messagegrn">{errors.partId}</div>
                                )}
                            </div>
                        </div>

                        <div className="form-group">
                            <label>Part Name:</label>
                            <div className="input-error-container">
                                <input type="text" value={selectedPart.partName} readOnly placeholder="Selected Part Name" className="read-only-input" />
                            </div>
                        </div>

                        <div className="form-group">
                            <label>Gross Total:</label>
                            <div className="input-error-container">
                                <input
                                    type="text"
                                    value={poData.total || 0}
                                    readOnly
                                    placeholder="0"
                                    className="read-only-input"
                                />
                            </div>
                        </div>

                        <div className="form-group">
                            <label>{`Quantity(${selectedPart.uom || ''}):`} <span className="required">*</span></label>
                            <div className="input-error-container">
                                <input
                                    type="text"
                                    name="qty"
                                    value={poData.qty || ""}
                                    onChange={handleChange}
                                    className={formSubmitted && errors.qty ? "error-input" : ""}
                                    disabled={!poData.partId}
                                />
                                {formSubmitted && errors.qty && (
                                    <div className="error-messagegrn">{errors.qty}</div>
                                )}
                            </div>
                        </div>

                        <div className="form-group">
                            <label>GST%:</label>
                            <div className="input-error-container">
                                <input
                                    type="text"
                                    value={poData.gst.toFixed(2) || "0.00"}
                                    readOnly
                                    placeholder="0.00"
                                    className="read-only-input"
                                />
                            </div>
                        </div>

                        <div className="form-group">
                            <label>GST Total:</label>
                            <div className="input-error-container">
                                <input
                                    type="text"
                                    value={poData.gstTotal.toFixed(2) || "0.00"}
                                    readOnly
                                    placeholder="0.00"
                                    className="read-only-input"
                                />
                            </div>
                        </div>

                        <div className="form-group">
                            <label>Due Date: <span className="required">*</span></label>
                            <div className="input-error-container">
                                <input
                                    type="date"
                                    name="dueDate"
                                    value={poData.dueDate || minDate}
                                    onChange={handleChange}
                                    min={minDate}
                                    onKeyDown={(e) => e.preventDefault()}
                                    disabled={readOnly}
                                    className={formSubmitted && errors.dueDate ? "error-input" : ""}
                                />
                                {formSubmitted && errors.dueDate && (
                                    <div className="error-messagegrn">{errors.dueDate}</div>
                                )}
                            </div>
                        </div>

                        <div className="form-group">
                            <label>IGST%:</label>
                            <div className="input-error-container">
                                <input
                                    type="text"
                                    value={poData.igst.toFixed(2) || "0.00"}
                                    readOnly
                                    placeholder="0.00"
                                    className="read-only-input"
                                />
                            </div>
                        </div>


                        <div className="form-group">
                            <label>Net Total:</label>
                            <div className="input-error-container">
                                <input
                                    type="text"
                                    value={poData.netTotal.toFixed(2) || "0.00"}
                                    readOnly
                                    placeholder="0.00"
                                    className="read-only-input"
                                />
                            </div>
                        </div>

                        <div className="form-group">
                            <label>Payment Terms: <span className="required">*</span></label>
                            <div className="input-error-container">
                                <input
                                    type="text"
                                    name="paymentTerms"
                                    value={poData.paymentTerms}
                                    onChange={handleChange}
                                    disabled={readOnly}
                                    className={formSubmitted && errors.paymentTerms ? "error-input" : ""}
                                />
                                {formSubmitted && errors.paymentTerms && (
                                    <div className="error-messagegrn">{errors.paymentTerms}</div>
                                )}
                            </div>
                        </div>

                    </div>

                    {permission.menus["country-masters"]?.create ?
                        <div className="button-container">
                            <button type="button" className="button cancel-button" onClick={handleCancel}>
                                Cancel
                            </button>
                            <button
                                type="submit"
                                className="button save-button"
                            >
                                {isProcessing ? "Saving..." : isEditing ? "Update Purchase Order" : "Save Purchase Order"}
                            </button>
                        </div> : <></>
                    }
                </form>
            </div>
        </>
    );
};

export default PurchaseOrderForm;